/* Query and mutation hooks for workspaces.
 *
 * Same shape as `features/auth/api`, and for the same reason: server state
 * lives in TanStack Query and nowhere else. There is no store holding "the
 * current Organisation" alongside this — a second copy is a second thing to
 * invalidate, and the one that gets forgotten is always the one on screen.
 */

import {
  acceptInvitationResponseSchema,
  type CreateOrganisationRequest,
  createOrganisationResponseSchema,
  type EnableValueRequest,
  enabledValuesResponseSchema,
  type InviteMemberRequest,
  invitationsResponseSchema,
  membersResponseSchema,
  organisationSummarySchema,
  ownJoinRequestsResponseSchema,
  pendingJoinRequestsResponseSchema,
  receivedInvitationsResponseSchema,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../../../shared/api/client.js';
import { CURRENT_USER_KEY } from '../../auth/index.js';
import { usageKey } from '../../plans/index.js';

/** The cached User, as much of it as this file needs to widen. */
interface CachedUser {
  readonly memberships: readonly unknown[];
}

const holdsMemberships = (value: unknown): value is CachedUser =>
  typeof value === 'object' && value !== null && Array.isArray((value as CachedUser).memberships);

export function useCreateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateOrganisationRequest) =>
      request('/organisations', {
        method: 'POST',
        schema: createOrganisationResponseSchema,
        body: values,
      }),
    onSuccess: ({ membership }) => {
      /* Appended rather than refetched: the response carries exactly what
       * `/auth/me` would add, so asking again is a round trip for an answer we
       * are already holding — the reasoning `useSignIn` records.
       *
       * Guarded rather than asserted, because the cache legitimately holds
       * `null` for a signed-out User and `undefined` before the first read. */
      queryClient.setQueryData(CURRENT_USER_KEY, (previous: unknown) =>
        holdsMemberships(previous)
          ? { ...previous, memberships: [...previous.memberships, membership] }
          : previous,
      );
    },
  });
}

export const organisationKey = (organisationId: string) =>
  ['organisations', organisationId] as const;

/**
 * One workspace, read back from the API.
 *
 * Read rather than remembered on purpose: the create response carries only the
 * Membership, so a screen fed from it alone has nothing to show somebody who
 * reloaded or arrived from a bookmark.
 */
export function useOrganisation(organisationId: string) {
  return useQuery({
    queryKey: organisationKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}`, { schema: organisationSummarySchema }),
    /* A 404 here means "not yours, or not there" and is a final answer, not a
     * transient failure. Retrying delays the redirect and asks the server the
     * same refused question three times. */
    retry: false,
  });
}

export const membersKey = (organisationId: string) =>
  ['organisations', organisationId, 'members'] as const;

/** Everyone in the workspace, with their Role. Any Member may read it. */
export function useMembers(organisationId: string) {
  return useQuery({
    queryKey: membersKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/members`, { schema: membersResponseSchema }),
    retry: false,
  });
}

export function useRenameOrganisation(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      request(`/organisations/${organisationId}`, {
        method: 'PATCH',
        schema: organisationSummarySchema,
        body: { name },
      }),
    onSuccess: (summary) => {
      /* Seeded rather than invalidated: the response IS the new summary, and
       * refetching would briefly show the old name in the topbar. */
      queryClient.setQueryData(organisationKey(organisationId), summary);
    },
  });
}

/**
 * How long inactive People are kept — STORY-043. Owner-only on the server.
 *
 * `null` switches erasure off and is the only way back to it, so the mutation
 * takes a nullable rather than a separate "disable" call: two endpoints for one
 * setting is two things that can disagree about what it currently is.
 */
export function useSetPersonRetention(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (months: number | null) =>
      request(`/organisations/${organisationId}/retention`, {
        method: 'PATCH',
        schema: organisationSummarySchema,
        body: { months },
      }),
    onSuccess: (summary) => {
      /* Seeded rather than invalidated: the response IS the new summary, and
       * refetching would briefly show the previous period in a control the
       * Owner just changed. Same reasoning as the rename above. */
      queryClient.setQueryData(organisationKey(organisationId), summary);
    },
  });
}

export function useChangeMemberRole(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      request(`/organisations/${organisationId}/members/${userId}`, {
        method: 'PATCH',
        schema: membersResponseSchema,
        body: { role },
      }),
    onSuccess: (roster) => {
      queryClient.setQueryData(membersKey(organisationId), roster);
      /* The caller may have just demoted THEMSELVES, which changes what the
       * navigation and this screen may offer. Their own Role lives on the
       * cached User, so it has to be re-read rather than assumed unchanged. */
      void queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    },
  });
}

export const invitationsKey = (organisationId: string) =>
  ['organisations', organisationId, 'invitations'] as const;

/** Open invitations. Owner-only on the server; the client never guesses. */
export function useInvitations(organisationId: string, enabled: boolean) {
  return useQuery({
    queryKey: invitationsKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/invitations`, {
        schema: invitationsResponseSchema,
      }),
    /* Not asked for at all as a Member: the server would answer 403, and a
     * failed request in the console is a worse way to express "not yours" than
     * simply not asking. The control is still the server's. */
    enabled,
    retry: false,
  });
}

export function useInviteMember(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: InviteMemberRequest) =>
      request(`/organisations/${organisationId}/invitations`, {
        method: 'POST',
        body: values,
      }),
    onSuccess: async () => {
      /* Invalidated rather than seeded: the response deliberately says nothing
       * about what happened, because inviting somebody already on the team must
       * be indistinguishable from inviting a stranger. The list is where the
       * truth is. */
      await queryClient.invalidateQueries({ queryKey: invitationsKey(organisationId) });
      await queryClient.invalidateQueries({ queryKey: usageKey(organisationId) });
    },
  });
}

export function useRevokeInvitation(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) =>
      request(`/organisations/${organisationId}/invitations/${invitationId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invitationsKey(organisationId) });
      await queryClient.invalidateQueries({ queryKey: usageKey(organisationId) });
    },
  });
}

export function useRemoveMember(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      request(`/organisations/${organisationId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: membersKey(organisationId) });
      /* A freed seat is available straight away — seats are currently active
       * Members, never the historical peak. */
      await queryClient.invalidateQueries({ queryKey: usageKey(organisationId) });
    },
  });
}

/**
 * Joining a team with either credential.
 *
 * The code is sent AS TYPED. One implementation of the folding rules — O for
 * zero, I and L for one, case and dashes — lives on the server, so the two
 * cannot disagree about what a code means.
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credential: { token: string } | { code: string } | { invitationId: string }) =>
      request('/invitations/accept', {
        method: 'POST',
        schema: acceptInvitationResponseSchema,
        body: credential,
      }),
    onSuccess: async () => {
      /* The User now holds a Membership they did not before, and the cached
       * copy is what the navigation and every guard read. */
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
      /* And the invitation they just used is no longer open, so the chooser's
       * list must not keep offering it — STORY-004. */
      await queryClient.invalidateQueries({ queryKey: RECEIVED_INVITATIONS_KEY });
    },
  });
}

/* ── belonging to several teams — STORY-009 ────────────────────────────────── */

/**
 * Makes one workspace the active one.
 *
 * There is no client-side "current organisation" for this to set, and that is
 * the design rather than an omission: the Active Organisation is the one named
 * in the URL, so two browser tabs on two workspaces each carry their own answer
 * and neither can move the other. What the call is FOR is the pair of things a
 * client cannot do for itself — re-verifying the Membership at the moment of
 * entry, and putting the switch in the server's audit trail.
 */
export function useSwitchOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organisationId: string) =>
      request(`/organisations/${organisationId}/switch`, {
        method: 'POST',
        schema: organisationSummarySchema,
        body: {},
      }),
    onSuccess: (summary) => {
      /* Seeded rather than invalidated: the response IS the summary the shell
       * is about to render, so the workspace it lands in has its name in the
       * topbar on the first frame instead of after a second round trip. */
      queryClient.setQueryData(organisationKey(summary.organisationId), summary);
    },
    onError: (error) => {
      /* A 404 here means the Membership went away while the chooser was on
       * screen — removed by an Owner, or left in another tab. The cached User
       * still lists it, so the row that just failed would sit there offering to
       * fail again. Re-reading is what makes the list tell the truth. */
      if (error instanceof ApiError && error.status === 404) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
      }
    },
  });
}

/**
 * Leaving a workspace. Available to every Role — the last-Owner guard is the
 * server's, and it answers 409 rather than 403.
 */
export function useLeaveOrganisation(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => request(`/organisations/${organisationId}/membership`, { method: 'DELETE' }),
    onSuccess: async () => {
      /* The Membership that every guard and the whole navigation read is gone,
       * so the cached User has to be re-read.
       *
       * The workspace's own queries are left ALONE rather than removed, which
       * is the opposite of what it looks like they need. `removeQueries` on a
       * query that still has a mounted observer triggers an immediate refetch
       * rather than silence — so clearing them here would fire exactly the
       * requests the server now answers 404 and 403 to, on the way out. The
       * caller navigates to the chooser a moment later and they unmount. */
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    },
  });
}

/* ── join requests — STORY-008 ─────────────────────────────────────────────── */

/** Account-level, not workspace-level: the caller is in no workspace yet. */
/**
 * Invitations addressed to the signed-in User — STORY-004.
 *
 * Distinct from `useInvitations`, which is the Owner's view of what a workspace
 * has SENT and is scoped to one organisation. This one is account-level, like
 * the join requests beside it, because the User is not yet a Member of any of
 * the workspaces it names.
 */
export const RECEIVED_INVITATIONS_KEY = ['invitations', 'mine'] as const;

export function useReceivedInvitations() {
  return useQuery({
    queryKey: RECEIVED_INVITATIONS_KEY,
    queryFn: () => request('/invitations/mine', { schema: receivedInvitationsResponseSchema }),
  });
}

export const OWN_JOIN_REQUESTS_KEY = ['join-requests'] as const;

export const joinRequestsKey = (organisationId: string) =>
  ['organisations', organisationId, 'join-requests'] as const;

export function useOwnJoinRequests() {
  return useQuery({
    queryKey: OWN_JOIN_REQUESTS_KEY,
    queryFn: () => request('/join-requests', { schema: ownJoinRequestsResponseSchema }),
  });
}

export function useRequestToJoin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organisationId: string) =>
      request('/join-requests', {
        method: 'POST',
        body: { organisationId },
      }),
    onSuccess: async () => {
      /* Refetched rather than appended, unlike `useCreateOrganisation`: the
       * response deliberately carries nothing — it is identical for a workspace
       * that does not exist — so there is no row to put in the cache. */
      await queryClient.invalidateQueries({ queryKey: OWN_JOIN_REQUESTS_KEY });
    },
  });
}

export function useWithdrawJoinRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) => request(`/join-requests/${requestId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: OWN_JOIN_REQUESTS_KEY });
    },
  });
}

export function usePendingJoinRequests(organisationId: string, enabled = true) {
  return useQuery({
    queryKey: joinRequestsKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/join-requests`, {
        schema: pendingJoinRequestsResponseSchema,
      }),
    /* Owner-only on the server, which answers 403 to a Member. Asking anyway
     * would put a red error in every Member's console on every load. */
    enabled,
  });
}

export function useApproveJoinRequest(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) =>
      request(`/organisations/${organisationId}/join-requests/${requestId}/approval`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: joinRequestsKey(organisationId) });
      /* A new Member: both the roster and the seat count moved. */
      await queryClient.invalidateQueries({ queryKey: membersKey(organisationId) });
      await queryClient.invalidateQueries({ queryKey: usageKey(organisationId) });
    },
  });
}

export function useDeclineJoinRequest(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) =>
      request(`/organisations/${organisationId}/join-requests/${requestId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: joinRequestsKey(organisationId) });
    },
  });
}

/* ── the enabled set — STORY-041 ───────────────────────────────────────────── */

export const enabledValuesKey = (organisationId: string) =>
  ['organisations', organisationId, 'enabled-values'] as const;

/**
 * What this workspace offers in its pickers.
 *
 * Readable by any Member, because every currency and timezone picker in the
 * product is filled from it — a Member who could not read it could not record
 * anything.
 */
export function useEnabledValues(organisationId: string) {
  return useQuery({
    queryKey: enabledValuesKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/enabled-values`, {
        schema: enabledValuesResponseSchema,
      }),
    retry: false,
  });
}

export function useEnableValue(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: EnableValueRequest) =>
      request(`/organisations/${organisationId}/enabled-values`, {
        method: 'POST',
        schema: enabledValuesResponseSchema,
        body: values,
      }),
    onSuccess: (set) => {
      /* Seeded rather than invalidated: every write answers with the set after
       * the change, so the list can never be left showing a state that was not
       * persisted — which is exactly what STORY-041 asks of the failure path. */
      queryClient.setQueryData(enabledValuesKey(organisationId), set);
    },
  });
}

export function useDisableValue(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: EnableValueRequest) =>
      request(
        `/organisations/${organisationId}/enabled-values?kind=${values.kind}&value=${encodeURIComponent(values.value)}`,
        { method: 'DELETE', schema: enabledValuesResponseSchema },
      ),
    onSuccess: (set) => {
      queryClient.setQueryData(enabledValuesKey(organisationId), set);
    },
  });
}
